/**
 * BioTurbiditySystem — escalating "stuff in the water" disturbance.
 *
 * From the start of phase 1 a faint cytoplasmic murk is visible, slowly
 * thickening as the player accumulates upgrade levels and as time elapses.
 * Visual ingredients:
 *   - drifting micro-debris (small dim blobs that float across screen)
 *   - radial vignette that creeps in from the edges
 *
 * Designed to read as atmospheric pressure, not punishing — center stays
 * clear so food/predators remain readable; the edges get progressively
 * obscured. Vignette is a one-shot Canvas2D radial gradient baked to a
 * Pixi texture so it costs nothing per frame.
 */

import { Container, Graphics, Sprite, Texture } from 'pixi.js';

interface Floater {
  g: Graphics;
  vx: number;
  vy: number;
  age: number;
  life: number;
  baseAlpha: number;
}

const COLORS = [0x2c4a55, 0x355163, 0x3a4d4f, 0x42483a, 0x483545];
const VIGNETTE_BAKED = 1024;     // square texture; stretched to screen
const FLOATER_TARGET_MIN = 4;
const FLOATER_TARGET_MAX = 36;

export class BioTurbiditySystem {
  private floaterLayer: Container;
  private vignette: Sprite;
  private floaters: Floater[] = [];
  private intensity = 0;
  private spawnAccum = 0;
  private vignetteOwned = true;

  constructor(worldLayer: Container, screenLayer: Container, screenW: number, screenH: number) {
    // Floaters live in the world layer so they share zoom + chroma post-fx
    // with the play scene. addChildAt(0) so they render BEHIND entities.
    this.floaterLayer = new Container();
    worldLayer.addChildAt(this.floaterLayer, 0);

    this.vignette = new Sprite(this.bakeVignette());
    this.vignette.width = screenW;
    this.vignette.height = screenH;
    this.vignette.alpha = 0;
    screenLayer.addChild(this.vignette);
  }

  /** Caller computes a 0..1 escalation level (upgrades + elapsed). */
  setIntensity(n: number): void {
    this.intensity = Math.max(0, Math.min(1, n));
  }

  update(dt: number, screenW: number, screenH: number): void {
    // Vignette: invisible at intensity 0 (no biomass threshold reached
    // yet), ramps up with intensity. Cap before fully blacking out the
    // edges — game still has to be playable.
    this.vignette.alpha = this.intensity * 0.50;
    this.vignette.width = screenW;
    this.vignette.height = screenH;

    // Spawn cadence — denser pool at higher intensity. At intensity 0 we
    // start with no floaters; they fade in once biomass clears the
    // threshold and the system gets a non-zero level.
    const target = this.intensity <= 0
      ? 0
      : Math.round(FLOATER_TARGET_MIN + this.intensity * (FLOATER_TARGET_MAX - FLOATER_TARGET_MIN));
    this.spawnAccum += dt;
    while (this.floaters.length < target && this.spawnAccum > 0.25) {
      this.spawnAccum -= 0.25;
      this.spawn(screenW, screenH);
    }

    // Tick existing floaters: drift, fade in/out, recycle.
    for (let i = this.floaters.length - 1; i >= 0; i--) {
      const f = this.floaters[i];
      f.age += dt;
      f.g.position.x += f.vx * dt;
      f.g.position.y += f.vy * dt;
      const t = f.age / f.life;
      const fade = t < 0.15 ? t / 0.15 : t > 0.85 ? (1 - t) / 0.15 : 1;
      f.g.alpha = f.baseAlpha * fade * (0.4 + this.intensity * 0.6);

      const off = f.g.position.x < -60 || f.g.position.x > screenW + 60
                || f.g.position.y < -60 || f.g.position.y > screenH + 60;
      if (f.age >= f.life || off) {
        f.g.destroy();
        this.floaters.splice(i, 1);
      }
    }
  }

  private spawn(w: number, h: number): void {
    const g = new Graphics();
    const size = 3 + Math.random() * 16;
    const color = COLORS[Math.floor(Math.random() * COLORS.length)];
    // Slightly irregular blob via two overlapping ellipses with offsets.
    g.ellipse(0, 0, size, size * (0.6 + Math.random() * 0.6))
      .fill({ color, alpha: 0.55 });
    g.ellipse(size * 0.25, -size * 0.15, size * 0.55, size * 0.4)
      .fill({ color: 0x000000, alpha: 0.18 });
    g.position.set(Math.random() * w, Math.random() * h);
    g.rotation = Math.random() * Math.PI * 2;
    g.alpha = 0;
    this.floaterLayer.addChild(g);

    const dirAngle = Math.random() * Math.PI * 2;
    const speed = 6 + Math.random() * 18;
    this.floaters.push({
      g,
      vx: Math.cos(dirAngle) * speed,
      vy: Math.sin(dirAngle) * speed,
      age: 0,
      life: 9 + Math.random() * 9,
      baseAlpha: 0.35 + Math.random() * 0.5,
    });
  }

  private bakeVignette(): Texture {
    const c = document.createElement('canvas');
    c.width = VIGNETTE_BAKED;
    c.height = VIGNETTE_BAKED;
    const ctx = c.getContext('2d')!;
    const cx = VIGNETTE_BAKED / 2;
    const grad = ctx.createRadialGradient(cx, cx, VIGNETTE_BAKED * 0.30, cx, cx, VIGNETTE_BAKED * 0.72);
    grad.addColorStop(0, 'rgba(2,8,14,0)');
    grad.addColorStop(0.55, 'rgba(2,8,14,0.30)');
    grad.addColorStop(1, 'rgba(2,8,14,0.95)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, VIGNETTE_BAKED, VIGNETTE_BAKED);
    return Texture.from(c);
  }

  destroy(): void {
    for (const f of this.floaters) f.g.destroy();
    this.floaters = [];
    this.floaterLayer.destroy({ children: true });
    if (this.vignetteOwned) this.vignette.destroy();
  }
}
